import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, Loader2, ChevronDown } from 'lucide-react';
import { resumeMaturity } from '../utils/maturityJobs';

const TYPES = { '/digital-maturity': 'digital', '/data-maturity': 'data', '/ai-maturity': 'ai',
  '/bpcm': 'bpcm', '/bpcm-assessment': 'bpcm', '/cyber-security': 'cyber', '/sustainability': 'sustainability',
  '/dashboard': 'company', '/report': 'value_scorecard' };
const LABELS = { checking: 'Independent check in progress', not_checked: 'Not independently checked',
  unavailable: 'Independent check unavailable', issues_detected: 'Discrepancies require attention',
  checked_with_limits: 'Review complete · evidence gaps remain', consistency_checked: 'Consistency checked' };

export const ReportQualityBar = () => {
  const location = useLocation();
  const [revision, setRevision] = useState(0);
  const [review, setReview] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const update = () => setRevision(n => n + 1);
    window.addEventListener('assessment-ready', update);
    return () => window.removeEventListener('assessment-ready', update);
  }, []);
  useEffect(() => {
    const ci = location.pathname.match(/^\/company-intelligence\/workspace\/([^/]+)$/);
    const kind = ci ? 'company_intelligence' : TYPES[location.pathname];
    const id = ci?.[1] || new URLSearchParams(window.location.search).get('id');
    setReview(null);
    if (!kind) return;
    if (!id) { resumeMaturity(kind); return; }
    let alive = true, timer;
    const load = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/assessment-quality/${kind}/${id}`);
        if (!alive) return;
        setReview(data);
        if (['checking', 'not_checked'].includes(data.status)) timer = setTimeout(load, 4000);
      } catch { if (alive) timer = setTimeout(load, 8000); }
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [location.pathname, location.search, revision]);
  if (!review) return null;
  const clear = review.status === 'consistency_checked';
  return <section data-testid="report-quality-bar" className={`border-b px-4 py-3 text-sm ${clear ? 'bg-emerald-50 text-emerald-950 border-emerald-200' : 'bg-amber-50 text-amber-950 border-amber-200'}`}>
    <div className="max-w-7xl mx-auto">
      <button data-testid="quality-details-toggle" onClick={() => setOpen(!open)} aria-expanded={open} className="flex flex-wrap w-full items-center gap-2 text-left hover:opacity-80 transition-opacity">
        {review.status === 'checking' ? <Loader2 className="w-4 h-4 animate-spin" /> : clear ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        <strong data-testid="quality-status">{LABELS[review.status] || 'Report check pending'}</strong>
        {review.critical_count > 0 && <span data-testid="quality-critical-count">{review.critical_count} critical</span>}
        {review.warning_count > 0 && <span data-testid="quality-warning-count">{review.warning_count} limitations</span>}
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div data-testid="quality-details" className="mt-3 space-y-2 break-words">
        <p data-testid="quality-summary">{review.summary}</p>
        <p data-testid="quality-evidence-scope">{review.scope || 'An AI check does not authenticate unsupported facts or replace an independent audit.'}</p>
        {review.judge_model && <p data-testid="quality-model">Reviewer: {review.judge_model} · Rubric: {review.rubric_version}</p>}
        {review.input_hash && <p data-testid="quality-fingerprint" className="text-xs">Input fingerprint: {review.input_hash.slice(0, 16)} · Corrections: {review.correction_count || 0}</p>}
      </div>}
    </div>
  </section>;
};